import { datadogRum } from "@datadog/browser-rum";
import type NutrientViewerType from "@nutrient-sdk/viewer";

import { mobile } from "@btnet/utilities-webview";

/**
 * The @nutrient-sdk/viewer JS entry wrapper is ~2 MB gzip — 24% of the main bundle when
 * imported statically, because any statically-reachable import pins it into the entry
 * chunk for every user (ado-281459). All runtime access therefore goes through the
 * dynamic-import wrapper below so Rollup splits the SDK into its own async chunk that
 * only downloads when a viewer actually mounts.
 *
 * Rules for consumers:
 *  - `import type` from "@nutrient-sdk/viewer" freely — types are erased at build time.
 *  - Never `import NutrientViewer from "@nutrient-sdk/viewer"` as a value outside this
 *    file; that re-pins the SDK into the entry chunk.
 *  - Await {@link loadNutrientViewer} before the first SDK use (typically in the load
 *    effect, before calling `NutrientViewer.load`). Code that provably runs after that —
 *    event handlers, imperative-handle methods, helpers operating on a live instance —
 *    can use the {@link NutrientViewer} proxy exactly like the old static import.
 */
let nutrientViewer: typeof NutrientViewerType | null = null;
let nutrientViewerPromise: Promise<typeof NutrientViewerType> | null = null;

export const loadNutrientViewer = (): Promise<typeof NutrientViewerType> => {
    nutrientViewerPromise ??= import("@nutrient-sdk/viewer").then((mod) => {
        nutrientViewer = mod.default;
        return mod.default;
    });
    return nutrientViewerPromise;
};

const getNutrientViewer = (): typeof NutrientViewerType => {
    if (!nutrientViewer) {
        throw new Error(
            "NutrientViewer accessed before loadNutrientViewer() resolved. Await loadNutrientViewer() first.",
        );
    }
    return nutrientViewer;
};

/**
 * Drop-in stand-in for the old `import NutrientViewer from "@nutrient-sdk/viewer"`:
 * property access is forwarded to the loaded SDK module, so existing call sites
 * (`NutrientViewer.ViewState`, `new NutrientViewer.Geometry.Rect(...)`, enums, etc.)
 * keep working unchanged. Throws if a property is read before
 * {@link loadNutrientViewer} has resolved — such a call site runs too early and must
 * await the load (or, for render-time enum reads, use the string mirrors below).
 */
export const NutrientViewer: typeof NutrientViewerType = new Proxy(
    {} as typeof NutrientViewerType,
    {
        get(_target, prop): unknown {
            return Reflect.get(getNutrientViewer(), prop);
        },
    },
);

/**
 * String-literal mirrors of SDK enum values that are read during React render — before
 * the SDK chunk may have loaded, where reading them off the module would throw (or force
 * an eager load). `satisfies` ties each literal to the SDK's own type so drift fails the
 * build. Post-load code should keep using the enums on the loaded module directly.
 */
export const NutrientAutoSaveMode = {
    INTELLIGENT: "INTELLIGENT",
} as const satisfies Partial<typeof NutrientViewerType.AutoSaveMode>;

export const NutrientDocumentComparisonSourceType = {
    USE_OPEN_DOCUMENT: "USE_OPEN_DOCUMENT",
} as const satisfies Partial<typeof NutrientViewerType.DocumentComparisonSourceType>;

// Tracks whether the one-time worker warm-up has been kicked off this session.
let workerPreloadStarted = false;

/**
 * Snapshot of `mobile.isWebview`, resolved on first use rather than at module
 * scope: in dev, `setupMobileMock` installs `sendToNative` on `window` during app
 * boot, which could land after this module evaluates. Memoizing the first read is
 * what guarantees every worker-initializing call in a session agrees — see
 * {@link getNutrientWebviewLoadOptions}.
 */
let isWebviewSnapshot: boolean | null = null;

/**
 * Half the SDK's standalone default of 1536. Nutrient allocates a full tile even where
 * it only partially covers the page, so a smaller grid both caps per-tile cost and
 * wastes fewer pixels at the page edges.
 */
const REDUCED_TILE_SIZE = 768;

/**
 * Worker-level load options applied when the viewer runs inside the mobile app
 * webview. **Every** call that initializes the WASM core — `preloadWorker`, every
 * `NutrientViewer.load()`, and `convertToPDF()` — must spread these, because
 * Nutrient primes the standalone worker once per session and asserts that later
 * configs match the first (e.g. "disableWebAssemblyStreaming was previously true,
 * but is now undefined"). The webview check is resolved internally instead of
 * passed in, so a call site cannot opt out and desync the session.
 *
 * Of the three options below, only `disableWebAssemblyStreaming` is actually in the
 * config key set Nutrient compares across a session (alongside `baseUrl`,
 * `baseCoreUrl`, `licenseKey`, `overrideMemoryLimit`, `customFonts` and a few others).
 * `standaloneInstancesPoolSize` and `tileSize` are per-`load()` options and are *not*
 * compared — which is why `reducedWebTileSize` can safely vary between call sites
 * without risking the desync this helper's internal webview check guards against.
 *
 * The mobile app webview runs on a tight per-process memory budget:
 *  - `standaloneInstancesPoolSize: 0` — don't keep a warm second WASM heap resident.
 *  - `disableWebAssemblyStreaming: true` — streaming compilation intermittently
 *    fails to instantiate the viewer in the webview ("can't open until restart").
 *  - `tileSize` — each tile costs tileSize² × devicePixelRatio² × 4 bytes; the
 *    standalone default (1536) on a dpr-3 phone is ~85 MB per tile and can OOM the
 *    webview while zooming a large plan (ado-277437). 768 cuts that to ~21 MB.
 *
 * `reducedWebTileSize` applies that same tile reduction on desktop web, where it is a
 * smaller but reproducible memory win — 13% on a light zoom pass, 33% at peak while
 * panning a zoomed plan — with no measured latency cost and identical sharpness (tiles
 * rasterize 1:1 with CSS pixels at either size). Desktop is not memory-capped, so it
 * is gated per builder behind `plansViewerReducedTileSize` while RUM long-task counts
 * are compared between cohorts (ado-282646). Callers that do not render for display
 * (e.g. the headless flatten) can leave it defaulted.
 */
export const getNutrientWebviewLoadOptions = (reducedWebTileSize = false) => {
    isWebviewSnapshot ??= mobile.isWebview;
    if (isWebviewSnapshot) {
        return {
            standaloneInstancesPoolSize: 0,
            disableWebAssemblyStreaming: true,
            tileSize: REDUCED_TILE_SIZE,
        };
    }
    return reducedWebTileSize ? { tileSize: REDUCED_TILE_SIZE } : {};
};

/** Test-only: clears the memoized webview snapshot so each case starts clean. */
export const resetNutrientWebviewSnapshotForTests = (): void => {
    isWebviewSnapshot = null;
};

/**
 * Warms the standalone WASM worker so the first `NutrientViewer.load()` — and any
 * loads that fire while it is still resolving (e.g. the two panes of the plan
 * side-by-side / comparison views) — reuse an already-fetched core instead of each
 * cold-instantiating it. Per the SDK, a `load()` called before the preload resolves
 * simply reuses the in-flight request, so warming once up front benefits every
 * viewer that mounts afterward. Safe to call repeatedly; it only warms once.
 *
 * Also fetches the SDK's JS chunk itself (via loadNutrientViewer), so calling this on
 * a page that will mount a viewer doubles as a chunk prefetch.
 *
 * The webview memory options come from {@link getNutrientWebviewLoadOptions}, the same
 * source every `NutrientViewer.load()` uses, so the primed worker config always matches
 * the load config. `reducedWebTileSize` is passed through for consistency with the
 * eventual load; tileSize is not part of the config set Nutrient compares, so a
 * mismatch here could not desync the session anyway.
 */
export const preloadNutrientWorker = (
    licenseKey: string | undefined,
    reducedWebTileSize = false,
): void => {
    if (workerPreloadStarted || !licenseKey) {
        return;
    }
    workerPreloadStarted = true;
    const baseUrl = `${window.location.protocol}//${window.location.host}${
        import.meta.env.BASE_URL
    }`;
    // preloadWorker only needs the worker/asset coordinates (licenseKey + baseUrl)
    // plus the memory options that must match the eventual load config;
    // `container`/`document` are required by the full load config but are unused when
    // preloading, so we cast rather than fabricate them.
    void loadNutrientViewer()
        .then((NutrientViewer) =>
            NutrientViewer.preloadWorker({
                licenseKey,
                baseUrl,
                ...getNutrientWebviewLoadOptions(reducedWebTileSize),
            } as unknown as Parameters<typeof NutrientViewer.preloadWorker>[0]),
        )
        .catch(() => {
            // Best-effort warm-up: if it fails, the normal load() path still works
            // (just without the head start). Allow a later call to retry.
            workerPreloadStarted = false;
        });
};

/**
 * Safely sets Nutrient breakpoint options to prevent toolbar icons from hiding
 * as the modal gets smaller, allowing users to scroll the toolbar horizontally.
 * Only sets the options if they haven't been set already (to avoid read-only property errors).
 *
 * Must be called after {@link loadNutrientViewer} has resolved (the SDK options are
 * global, so viewers call this in their load path rather than at module scope).
 */
export const configureNutrientBreakpoints = (): void => {
    const NutrientViewer = getNutrientViewer();
    const smToolbarDescriptor = Object.getOwnPropertyDescriptor(
        NutrientViewer.Options,
        "BREAKPOINT_SM_TOOLBAR",
    );
    const mdToolbarDescriptor = Object.getOwnPropertyDescriptor(
        NutrientViewer.Options,
        "BREAKPOINT_MD_TOOLBAR",
    );

    if (!smToolbarDescriptor || smToolbarDescriptor.writable !== false) {
        NutrientViewer.Options.BREAKPOINT_SM_TOOLBAR = 0;
    }
    if (!mdToolbarDescriptor || mdToolbarDescriptor.writable !== false) {
        NutrientViewer.Options.BREAKPOINT_MD_TOOLBAR = 0;
    }
};

/**
 * Strips the wrapping `<div>` that BDS icons render around their `<svg>`, then
 * replaces the SVG's baked-in `width="1em" height="1em"` and generated `id`
 * with the requested pixel dimensions. Without this, the duplicate width/height
 * attributes leave the original `1em` values in force and the requested size
 * is silently ignored.
 */
/**
 * NutrientViewer / PSPDFKit instance methods throw a specific class of errors
 * when called after the underlying document has been unloaded — typically
 * because the user closed the viewer (or it was unmounted) between
 * `const instance = instanceRef.current` and an `await instance.someMethod(...)`
 * further down the same flow. The local handle is still object-valid, but
 * the document is gone, so the SDK throws.
 *
 * Known message variants observed in Sentry:
 *   - "no open document"
 *   - "No document is loaded"
 *   - "This method can not be called since there is no open document. Have you run NutrientViewer.unload()?"
 *
 * Use this predicate to swallow those benign races; re-throw anything else.
 *
 * Fixes BTNET-REACT-B9PN ("No document is loaded") and BTNET-REACT-BA57
 * ("This method can not be called since there is no open document").
 */
export const isPSPDFKitDocumentClosedError = (e: unknown): boolean => {
    const msg = String((e as { message?: string } | null)?.message ?? e);
    return (
        msg.includes("no open document") ||
        msg.includes("No document is loaded") ||
        msg.includes("there is no open document")
    );
};

/**
 * InstantJSON is a *diff* against a specific PDF, not a standalone snapshot. Two of
 * its fields are expressed in that PDF's object-id space:
 *
 *  - `pdfId` — the identity of the PDF the diff was recorded against. Nutrient
 *    compares it to the loaded document and rejects the import on a mismatch.
 *  - `skippedPdfObjectIds` — "these native PDF annotations should no longer exist".
 *    On import Nutrient must *delete* each one to reproduce the recorded state, and
 *    the whole import throws if any delete fails.
 *
 * Historically we stripped only `pdfId`, to get past the identity check when a page
 * file had been regenerated. That kept the absolute object-id references while
 * discarding the identity they refer to — so instead of a clean "wrong PDF"
 * rejection, a stale payload reached the WASM core and died there with
 * "Some annotations couldn't be deleted. (pdf object ids: {...})", taking the whole
 * viewer load down with it (ado-283518, Sentry BTNET-REACT-BA5X / BJW5).
 *
 * The two fields have to be dropped together: once the identity guard is gone, no
 * object-id reference in the payload can be trusted. Dropping `skippedPdfObjectIds`
 * means the document's own native annotations survive the import, which is the safe
 * direction — a stray leftover annotation beats an unopenable plan.
 *
 * Returns a shallow copy; the caller's object is not mutated.
 */
export const prepareInstantJSONForImport = <T extends object>(instantJSON: T): T => {
    const {
        pdfId: _pdfId,
        skippedPdfObjectIds: _skippedPdfObjectIds,
        ...rest
    } = instantJSON as T & {
        pdfId?: unknown;
        skippedPdfObjectIds?: unknown;
    };
    return rest as T;
};

/**
 * Matches the errors Nutrient throws when an InstantJSON/XFDF annotation payload
 * can't be applied to the document being loaded — as opposed to the document
 * itself being unloadable.
 *
 * Observed message variants:
 *   - "Error on importing Instant Document JSON: Encountered N error(s) importing JSON:"
 *   - "Some annotations couldn't be deleted. (pdf object ids: {...})"
 *
 * These are recoverable: the PDF is fine, only the markup layer is bad, so callers
 * can retry the load without annotations rather than failing outright.
 */
export const isPSPDFKitAnnotationImportError = (e: unknown): boolean => {
    const msg = String((e as { message?: string } | null)?.message ?? e);
    return (
        msg.includes("importing Instant Document JSON") ||
        msg.includes("importing JSON") ||
        msg.includes("couldn't be deleted")
    );
};

/**
 * How long {@link whenContainerIsSized} waits for a container to gain both dimensions
 * before loading anyway. Comfortably clears a modal open animation (~300ms) while
 * keeping the fallback wait short enough that a genuinely unsizeable host doesn't
 * leave the user staring at a spinner.
 */
export const CONTAINER_SIZE_TIMEOUT_MS = 2000;

/**
 * Defers a Nutrient `load()` until its container has a non-zero width **and** height.
 *
 * Nutrient measures the container when it mounts and bails with
 * `FrameProvider: container does not have a width or height` when either dimension is
 * 0 — the viewer attaches but paints nothing, so the user gets a blank preview pane
 * (ado-285352).
 *
 * Viewer containers size themselves with percentages (`width: 100%; height: 100%`).
 * A block-level container reports its full width the moment its parent has one, but a
 * percentage height stays 0 until an ancestor resolves a *definite* height — which
 * modal open animations, split-view panels, and auto-height flex ancestors only do a
 * frame or more later. Gating the load on width alone therefore fires early and mounts
 * into a 0-height box, and because the gate then stops observing it never re-measures.
 *
 * `onReady` runs at most once. If the container is still unsized after
 * {@link CONTAINER_SIZE_TIMEOUT_MS} it runs anyway — a host that can never size its
 * container should get today's behavior rather than a viewer that silently never loads
 * — and the timeout is reported to Datadog so that host can be found and fixed.
 *
 * @returns cleanup to call on effect teardown.
 */
export const whenContainerIsSized = (container: HTMLElement, onReady: () => void): (() => void) => {
    let observer: ResizeObserver | null = null;
    let timeoutId: number | undefined;
    let hasFired = false;

    const cleanup = () => {
        observer?.disconnect();
        observer = null;
        clearTimeout(timeoutId);
        timeoutId = undefined;
    };

    const fire = (timedOut: boolean) => {
        if (hasFired) {
            return;
        }
        hasFired = true;
        cleanup();

        if (timedOut) {
            datadogRum.addError(
                new Error("Nutrient viewer container never gained a width and height"),
                {
                    clientWidth: container.clientWidth,
                    clientHeight: container.clientHeight,
                    timeoutMs: CONTAINER_SIZE_TIMEOUT_MS,
                },
            );
        }

        onReady();
    };

    // Measure the live box rather than the entry's contentRect: clientWidth/clientHeight
    // are what Nutrient itself checks, so the gate can't disagree with the SDK.
    observer = new ResizeObserver(() => {
        if (container.clientWidth > 0 && container.clientHeight > 0) {
            fire(false);
        }
    });
    observer.observe(container);

    timeoutId = window.setTimeout(() => fire(true), CONTAINER_SIZE_TIMEOUT_MS);

    return cleanup;
};

export const extractBdsSvgIcon = (iconString: string, height: number, width: number) => {
    const svgExtract = /<div.*?>(<svg.*?<\/svg>)<\/div>/;

    return iconString
        .replace(svgExtract, "$1")
        .replace(/\s+(?:width|height|id)="[^"]*"/g, "")
        .replace(/<svg/, `<svg width="${width}" height="${height}"`);
};
